import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { useCms } from "./CmsContext";

export function useMediaLibrary() {
  const { api, API_BASE } = useCms();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    const res = await api.get(`${API_BASE}/media`);
    if (res && res.success) setFiles(res.files || []);
    setLoading(false);
  }, [api, API_BASE]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const upload = useCallback(
    async (fileList) => {
      const filesArr = Array.from(fileList || []).filter((f) =>
        f.type.startsWith("image/")
      );
      if (filesArr.length === 0) return [];
      setUploading(true);
      const uploaded = [];
      for (const file of filesArr) {
        const formData = new FormData();
        formData.append("image", file);
        const res = await api.post(`${API_BASE}/media/upload`, formData);
        if (res && res.success && res.imageUrl) {
          uploaded.push(res.imageUrl);
        } else {
          toast.error(`Upload failed: ${file.name}`);
        }
      }
      setUploading(false);
      if (uploaded.length > 0) {
        toast.success(`Uploaded ${uploaded.length} image${uploaded.length > 1 ? "s" : ""}`);
        await refresh();
      }
      return uploaded;
    },
    [api, API_BASE, refresh]
  );

  const remove = useCallback(
    async (fileName) => {
      const res = await api.post(`${API_BASE}/media/delete`, { fileName });
      if (res && res.success) {
        toast.success("Image deleted");
        await refresh();
      } else {
        toast.error(res?.error || "Delete failed");
      }
    },
    [api, API_BASE, refresh]
  );

  return { files, loading, uploading, refresh, upload, remove };
}

export function MediaGrid({ files, onSelect, onDelete, selectable }) {
  if (files.length === 0) {
    return <p className="lw-empty">No uploads yet. Drop images here or use the upload button.</p>;
  }
  return (
    <div className="lw-mediagrid">
      {files.map((f) => (
        <figure key={f.fileName} className="lw-mediagrid__item">
          <button
            type="button"
            className="lw-mediagrid__thumb"
            title={selectable ? "Use this image" : f.fileName}
            onClick={() => onSelect && onSelect(f.url)}
          >
            <img src={f.url} alt={f.fileName} loading="lazy" />
          </button>
          <figcaption className="lw-mediagrid__meta">
            <span className="lw-mediagrid__name" title={f.fileName}>
              {f.fileName}
            </span>
            <span className="lw-mediagrid__actions">
              <button
                type="button"
                className="lw-iconbtn"
                title="Copy URL"
                onClick={() => {
                  navigator.clipboard.writeText(f.url);
                  toast.success("URL copied");
                }}
              >
                ⧉
              </button>
              {onDelete && (
                <button
                  type="button"
                  className="lw-iconbtn lw-iconbtn--danger"
                  title="Delete"
                  onClick={() => {
                    if (window.confirm(`Delete ${f.fileName}? Pages still referencing it will break.`)) {
                      onDelete(f.fileName);
                    }
                  }}
                >
                  ×
                </button>
              )}
            </span>
          </figcaption>
        </figure>
      ))}
    </div>
  );
}

export function UploadDropzone({ onFiles, uploading }) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  return (
    <div
      className={`lw-dropzone ${dragOver ? "lw-dropzone--over" : ""}`}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        onFiles(e.dataTransfer.files);
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={(e) => {
          onFiles(e.target.files);
          e.target.value = "";
        }}
      />
      <p className="lw-dropzone__text">
        {uploading ? "Uploading…" : "Drag & drop images here, or"}
      </p>
      {!uploading && (
        <button type="button" className="lw-btn lw-btn--secondary lw-btn--sm" onClick={() => inputRef.current?.click()}>
          Browse files
        </button>
      )}
    </div>
  );
}

export function MediaPickerModal({ onSelect, onClose }) {
  const { files, loading, uploading, upload } = useMediaLibrary();
  const [query, setQuery] = useState("");

  const filtered = files.filter((f) =>
    f.fileName.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div className="lw-modal" role="dialog" aria-modal="true" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="lw-modal__panel lw-modal__panel--wide">
        <div className="lw-modal__head">
          <h2 className="lw-modal__title">Choose an image</h2>
          <button type="button" className="lw-iconbtn" title="Close" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="lw-modal__body">
          <div className="lw-mediapicker__bar">
            <input
              className="lw-input"
              type="search"
              placeholder="Search uploads…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <UploadDropzone
            uploading={uploading}
            onFiles={async (fileList) => {
              const uploaded = await upload(fileList);
              if (uploaded.length === 1) onSelect(uploaded[0]);
            }}
          />
          {loading ? (
            <p className="lw-empty">Loading media…</p>
          ) : (
            <MediaGrid files={filtered} onSelect={onSelect} selectable />
          )}
        </div>
      </div>
    </div>
  );
}
